// MACHINERY V2 (Stroje) — Servisné správy: the QUERY, the guard and the failure
// state (M2.5; split out of ServiceReportsTabV2.tsx at the M2.5 review for the
// 300-line cap).
//
// ServiceReportsTabV2.tsx keeps the STATES and the pixels; this file owns what
// is asked of the server and which machine the answer belongs to. The
// client-side status filtering, and why the fetch never sends `statusCodes`,
// are documented in that file's header — do not re-derive them here.
//
// THE IDENTITY GUARD (review finding R1). `customerServiceReports.serviceReports`
// is app-wide and nothing clears it, so the previous machine's reports render
// under this one until the new response lands. `reports` and `hasAny` below are
// therefore gated on `useMachineScope` — the same rule MachineProfileV2.tsx:137-142
// applies to `customerMachinery.machine` and SpecificationsTabV2.tsx:86 applies
// to `selectedModel`. See useMachineScope.ts for the full reasoning; it is the
// REQUIRED pattern for any v2 surface reading an app-wide slice keyed by machine.
//
// NOTHING SURVIVES A MACHINE CHANGE — DATA, SELECTION, OR SORT (review finding
// R3, and it is doctrine for every v2 tab). The machine-change effect re-fetches
// with OpenDate/desc, so search, status AND both sort keys reset to that same
// default. Leaving `sortBy` on a previously-clicked column made the header
// advertise a sort the rows did not have, and the next search re-sent it.
//
// THE DEBOUNCE READS THE SORT FROM A REF, AND SORTING CANCELS THE DEBOUNCE
// (review finding C2). `handleSearch` schedules a fetch 300 ms out with the sort
// captured at keystroke time; `handleSort` fetches immediately. Typing and then
// clicking a column inside those 300 ms used to run the sorted fetch first and
// let the stale closure re-fetch with the OLD sort — the list silently reverted
// under a header that still showed the new column, and `lastQueryRef` (what
// Retry re-runs) was left holding the wrong query. `sortRef` + the cancel are
// the sibling pattern the parts side already uses with its `queryRef`
// (usePartsTabQuery.ts).
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import { getServiceReports } from "../../../customerMachineServiceReportsSlice";
import {
  ServiceReport,
  ServiceReportStatusCode,
  ServiceReportsQuery,
  ServiceReportsQueryParameter,
} from "../../../models/ServiceReportsModel";
import { useMachineScope } from "./useMachineScope";

export type SrStatusFilter = ServiceReportStatusCode | null;

/** The one sort every machine starts on — newest first (artboard 1000). */
const DEFAULT_SORT = {
  sortBy: ServiceReportsQueryParameter.OpenDate,
  sortDesc: true,
};

const SEARCH_DEBOUNCE_MS = 300;

export interface SrTabQuery {
  search: string;
  status: SrStatusFilter;
  setStatus: (next: SrStatusFilter) => void;
  sortBy: ServiceReportsQueryParameter;
  sortDesc: boolean;
  /** THIS machine's reports, or undefined while the shared slot holds another
   *  machine's list (or before the first response). */
  reports?: ServiceReport[];
  /** Same gate: the slice-wide flag is only readable when the slot is ours. */
  hasAny?: boolean;
  loading: boolean;
  failed: boolean;
  handleSearch: (value: string) => void;
  handleSort: (key: ServiceReportsQueryParameter) => void;
  clearFilters: () => void;
  refetch: () => void;
}

export const useSrTabQuery = (machineId: string): SrTabQuery => {
  const dispatch = useAppDispatch();
  const reports = useAppSelector((s) => s.customerServiceReports.serviceReports);
  const loading = useAppSelector((s) => s.customerServiceReports.loading);
  const hasAny = useAppSelector((s) => s.customerServiceReports.hasAnyServiceReports);
  const { owns, failed, track } = useMachineScope(machineId);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<SrStatusFilter>(null);
  const [sortBy, setSortBy] = useState<ServiceReportsQueryParameter>(DEFAULT_SORT.sortBy);
  const [sortDesc, setSortDesc] = useState(DEFAULT_SORT.sortDesc);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The query the last fetch used — what Retry re-runs. */
  const lastQueryRef = useRef<ServiceReportsQuery>({});
  /** Mirrors the sort for the debounced closure. Written in an effect, never
   *  during render (the PartsTabV2 `queryRef` rule). */
  const sortRef = useRef({ sortBy, sortDesc });
  useEffect(() => {
    sortRef.current = { sortBy, sortDesc };
  }, [sortBy, sortDesc]);

  const runFetch = useCallback(
    (query: ServiceReportsQuery) => {
      lastQueryRef.current = query;
      track(machineId, dispatch(getServiceReports({ machineId, query })).unwrap());
    },
    [dispatch, machineId, track]
  );

  const refetch = useCallback(() => runFetch(lastQueryRef.current), [runFetch]);

  useEffect(() => {
    setSearch("");
    setStatus(null);
    setSortBy(DEFAULT_SORT.sortBy);
    setSortDesc(DEFAULT_SORT.sortDesc);
    sortRef.current = { ...DEFAULT_SORT };
    runFetch({ ...DEFAULT_SORT });
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [machineId, runFetch]);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => runFetch({ search: value || undefined, ...sortRef.current }),
      SEARCH_DEBOUNCE_MS
    );
  };

  const handleSort = (key: ServiceReportsQueryParameter) => {
    const desc = sortBy === key ? !sortDesc : true;
    setSortBy(key);
    setSortDesc(desc);
    // The pending keystroke fetch would otherwise land AFTER this one with the
    // previous sort. This handler already carries the current search, so
    // cancelling loses nothing.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runFetch({ search: search || undefined, sortBy: key, sortDesc: desc });
  };

  const clearFilters = () => {
    setSearch("");
    setStatus(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runFetch({ sortBy, sortDesc });
  };

  return {
    search,
    status,
    setStatus,
    sortBy,
    sortDesc,
    reports: owns ? reports : undefined,
    hasAny: owns ? hasAny : undefined,
    loading,
    failed,
    handleSearch,
    handleSort,
    clearFilters,
    refetch,
  };
};

export default useSrTabQuery;
